import React from 'react';
import {
  FieldContext,
  TagOption,
} from '../../../../../utils/apollo/graphqlSchema';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import {
  assertCondition,
  assertNotNil,
  isNotNil,
} from '../../../../../utils/types';
import { setBreakdownGroupField } from '../../../../../redux/slices/oldBuilder';
import EntitySelection from '../../../EntitySelection';
import {
  asSingleSelection,
  createTagOptionInput,
} from '../../../EntitySelection/utils';
import { ISelectedEntity } from '../../../EntitySelection/types';
import { useActionsVisibilityContext } from '../../../../ActionsVisibilityContext';
import { activeSchemaIdSelector } from '../../../../../redux/selectors/builder';
import {
  EntityDropzoneDataTypeEnum,
  IBreakdownDropzoneProperties,
} from '../../../../DndEntity/validation';
import FieldDroppableZone from '../../../../DndEntity/DndDroppableZone/FieldDroppableZone';
import { getDndActiveItemData } from '../../../../DndEntity/utils';
import { useDndEntityInternalContext } from '../../../../DndEntity';

interface IProps {
  groupIndex: number;
}

const tags = [
  createTagOptionInput(TagOption.Field, {
    field: {
      events: [],
      func: null,
      context: FieldContext.Breakdown,
      arrayObjectContext: null,
    },
  }),
];

export default ({ groupIndex }: IProps) => {
  const dispatch = useAppDispatch();
  const activeSchemaId = useAppSelector(activeSchemaIdSelector);

  const groups = useAppSelector(({ builder }) => builder.breakdown.groups);
  const groupFields = groups.map((group) => group.field).filter(isNotNil);
  const currentGroup = groups[groupIndex];
  assertNotNil(currentGroup);

  const { hideActions } = useActionsVisibilityContext();
  const { activeItem } = useDndEntityInternalContext();

  const handleRemove = () => {
    dispatch(setBreakdownGroupField({ groupIndex, field: null }));
  };

  const handleChange = (newValue: ISelectedEntity) => {
    assertCondition(newValue.__typename === 'Field');
    dispatch(setBreakdownGroupField({ groupIndex, field: newValue }));
  };

  const dropzoneId = `Breakdown-Field-Dropzone-${groupIndex}`;

  const hasErrors = () => {
    const data = getDndActiveItemData(activeItem);
    if (data && data.__typename === 'TagLeafField') {
      return groups.some((group) => group.field?.key === data.key);
    }
    return false;
  };

  const getCustomMessage = () => {
    const data = getDndActiveItemData(activeItem);
    if (
      data &&
      data.__typename === 'TagLeafField' &&
      groups.some((group) => group.field?.key === data.key)
    ) {
      return `Breakdown already contains ${data.name}`;
    }
  };

  return (
    <FieldDroppableZone
      dropzoneId={dropzoneId}
      tags={tags}
      data={
        {
          id: dropzoneId,
          type: EntityDropzoneDataTypeEnum.Breakdowns,
          groupIndex,
        } satisfies IBreakdownDropzoneProperties
      }
      hasError={hasErrors()}
      customMessage={getCustomMessage()}
    >
      <EntitySelection
        schemaId={activeSchemaId}
        selectedEntities={asSingleSelection(currentGroup.field)}
        disabledEntities={groupFields}
        tags={tags}
        onOpen={hideActions}
        onRemoveEntity={handleRemove}
        onSelectEntity={handleChange}
        placeholder={'Select field to breakdown'}
        autoFocus={!currentGroup.field}
      />
    </FieldDroppableZone>
  );
};
